/**
 * Title transfers: the buyer's offers, and the seller unsaying or unlocking one.
 */
import type {
  AnswerTransferRequest,
  EffectOwnerSaleRequest,
  TransferOffersResponse,
} from "@ondo/shared";

import { request } from "./request.ts";

/**
 * The OWNER recording a sale of their own work — OND-173.
 *
 * SCOPED TO THE WORK, not to a consignment, because there is no consignment to
 * scope it to: this is a collector selling privately, and `ondo.effect_owner_sale()`
 * resolves the authority from the owner-level grant on the collection holding
 * the piece instead.
 *
 * `effectSale` is the gallery's half and lives in `consignments.ts` beside the
 * row that authorises it. The two are deliberately separate calls on separate
 * resources rather than one call with an optional consignment: `OwnerSaleController`
 * is a controller of its own for the same reason, and an owner's sale takes no
 * covenant arguments at all.
 *
 * THERE IS NO `buyer-check` TO CALL BEFORE THIS. `ondo.transferee_check()` takes
 * a consignment, so a wrong ONDO id meets one opaque refusal here at submit and
 * not on blur of the field. That is the designed behaviour and not a gap the
 * client may paper over with a second resolver.
 */
export const effectOwnerSale = (workId: string, body: EffectOwnerSaleRequest) =>
  request<void>(`/works/${workId}/sale`, body);

/**
 * Title somebody has offered you and you have not answered.
 *
 * The BUYER'S side of the row `fetchGalleryPlacements` shows the gallery. Only
 * `status = 'offered'` rows appear: an off-registry sale has nobody to ask, so
 * it never becomes anybody's inbox item.
 */
export const fetchTransferOffers = () =>
  request<TransferOffersResponse>("/transfers/offers").then((body) => body.offers);

/**
 * Accepting or declining title.
 *
 * `collectionId` is the BUYER'S and they must own it — required on an
 * acceptance, meaningless on a decline. A registry that chose where somebody's
 * new picture went would be deciding a thing only they can.
 */
export const answerTransfer = (transferId: string, body: AnswerTransferRequest) =>
  request<void>(`/transfers/${transferId}/answer`, body);

/** The effecting party unsaying a sale. The row survives; the work goes green. */
export const withdrawTransfer = (id: string) =>
  request<void>(`/transfers/${id}/withdraw`, {});

/** Lifting a covenant's lock. Records the release; never edits the term. */
export const unlockCovenant = (id: string) =>
  request<void>(`/transfers/${id}/unlock`, {});
